<template>
    <div>
        <div class="kgr-row">
            <kgr-note-box
                title-key="stationeries.upload.noteBox.title"
                text-key="stationeries.upload.noteBox.text"
                html
            />
        </div>

        <h3 class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
            {{ $t('Verwendbar in Rechnungen und Mahnungen') }}
        </h3>

        <kgr-empty-data
            v-if="invoiceStationeries.length === 0 && !loading"
            :value="$t('Keine Rechnungsbriefpapiere vorhanden')"
        />
        <kgr-table
            v-else-if="invoiceStationeries.length > 0"
            style="width: 100%"
            :is-data-table="false"
        >
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Name des Briefpapiers') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Optionen') }}
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="(stationery, index) in invoiceStationeries"
                    :key="index"
                    class="kgr-table__row"
                    :class="{ 'kgr-table__row--deleted': stationery.deleted }"
                >
                    <td class="kgr-table__cell">
                        {{ stationery.name }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--actions">
                        <a
                            v-if="stationery.isAllowedToEdit"
                            :href="`/backend/clubs/stationery/${stationery.id}/edit`"
                            class="kgr-table__action"
                        >
                            <kgr-icon-edit
                                :title="$t('stationery.icon.edit.title')"
                            />
                        </a>

                        <button v-if="stationery.isAllowedToEdit"
                                type="button"
                                class="kgr-table__action"
                                @click="stationeryForDeletion = stationery"
                                :title="$t('Briefpapier löschen')">
                            <kgr-icon-delete />
                        </button>
                    </td>
                </tr>
            </tbody>
        </kgr-table>

        <template v-if="invoiceStationeries.length > 0">
            <div class="spacer"></div>
            <div class="kgr-row">
                <kgr-note-box
                    title-key="clubStationeries.invoice.noteBox.title"
                    text-key="clubStationeries.invoice.noteBox.text"
                    html
                />
            </div>
            <kgr-loading v-if="loading" />
            <kgr-table
                style="width: 100%"
                :is-data-table="false"
            >
                <thead>
                    <tr class="kgr-table__row">
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Aktiv') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Rechnungsvorlage') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Typ / Verwendungszweck') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Briefpapier') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(template) in templates"
                        :key="template.type"
                        class="kgr-table__row"
                    >
                        <td class="kgr-table__cell">
                            <kgr-checkbox
                                v-model="template.active"
                                class="kgr-row mt1"
                                :readonly="template.type === 'invoice_default'"
                                :inactive="template.type === 'invoice_default'"
                            />
                        </td>
                        <td class="kgr-table__cell">
                            {{ template.name }}
                        </td>
                        <td class="kgr-table__cell">
                            {{ $t(`clubInvoiceTemplate.type.${template.type}`) }}
                        </td>
                        <td class="kgr-table__cell">
                            <kgr-dropdown
                                :value="invoiceStationeries.find(s => s.id === template.stationeryId)"
                                :default-text="$t('Kein Briefpapier')"
                                :options="invoiceStationeries"
                                display="name"
                                :disabled="!canSave"
                                @post-input="setStationery(template, $event)"
                            />
                        </td>
                    </tr>
                </tbody>
            </kgr-table>
            <br />
            <div
                class="app-formFooter__actions"
            >
                <div class="app-formFooter__action"></div>
                <div class="app-formFooter__action">
                    <kgr-button
                        v-if="canSave"
                        :value="$t('saveChanges')"
                        type="blue"
                        :pending="pending"
                        @click="save"
                    />
                </div>
            </div>
        </template>
        <hr class="kgr-separator kgr-separator--light" />

        <kgr-modal
            :headline="$t('note')"
            :value="stationeryForDeletion !== null"
            @close="stationeryForDeletion = null"
            v-if="stationeryForDeletion !== null"
        >
            {{ stationeryForDeletion.type === 'invoice' ? $t('popup.delete.clubStationery.invoice') : $t('Das Briefpapier wird möglicherweise in Dokumentenvorlagen benutzt. Möchtest Du es trotzdem löschen?') }}
            <template #footer>
                <kgr-button @click="stationeryForDeletion = null" type="white">
                    {{ $t('Abbrechen') }}
                </kgr-button>
                <kgr-button @click="deleteStationery(stationeryForDeletion)">
                    {{ $t('Briefpapier löschen') }}
                </kgr-button>
            </template>
        </kgr-modal>

        <h3 class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
            {{ $t('Verwendbar in Dokumentenvorlagen') }}
        </h3>

        <kgr-empty-data
            v-if="simpleStationeries.length === 0 && !loading"
            :value="$t('Keine Briefpapiere vorhanden')"
        />
        <kgr-table
            v-else-if="simpleStationeries.length > 0"
            style="width: 100%"
            :is-data-table="false"
        >
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Name des Briefpapiers') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Optionen') }}
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="(stationery, index) in simpleStationeries"
                    :key="index"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell">
                        {{ stationery.name }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--actions">
                        <a
                            v-if="stationery.isAllowedToEdit"
                            :href="`/backend/clubs/stationery/${stationery.id}/edit`"
                            class="kgr-table__action"
                        >
                            <kgr-icon-edit
                                :title="$t('stationery.icon.edit.title')"
                            />
                        </a>

                        <button v-if="stationery.isAllowedToEdit"
                                type="button"
                                class="kgr-table__action"
                                @click="stationeryForDeletion = stationery"
                                :title="$t('Briefpapier löschen')">
                            <kgr-icon-delete />
                        </button>
                    </td>
                </tr>
            </tbody>
        </kgr-table>
    </div>
</template>

<script>
import Checkbox from '../../../components/common/form/checkbox/Checkbox.vue';
import ClubInvoiceTemplateRepository from '../../../js/repository/ClubInvoiceTemplateRepository';
import ClubStationeryRepository from '../../../js/repository/ClubStationeryRepository';
import Dropdown from '../../../components/common/form/select/Dropdown.vue';
import EmptyData from '../../../components/common/EmptyData.vue';
import Error from '../../../js/model/Error';
import GenericModal from '../../../components/common/modal/GenericModal.vue';
import IconDelete from '../../../components/common/icon/IconDelete.vue';
import IconEdit from '../../../components/common/icon/IconEdit.vue';
import KgrButton from '../../../components/common/form/button/KgrButton';
import Loading from '../../../components/common/Loading';
import NoteBox from '../../../components/common/NoteBox.vue';
import Table from '../../../components/common/table/Table.vue';

export default {
    components: {
        'kgr-button': KgrButton,
        'kgr-loading': Loading,
        'kgr-empty-data': EmptyData,
        'kgr-note-box': NoteBox,
        'kgr-dropdown': Dropdown,
        'kgr-modal': GenericModal,
        'kgr-icon-delete': IconDelete,
        'kgr-table': Table,
        'kgr-icon-edit': IconEdit,
        'kgr-checkbox': Checkbox,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            error: new Error(),
            loading: true,
            pending: false,
            stationeryForDeletion: null,
            templates: [],
            simpleStationeries: [],
            invoiceStationeries: [],
        };
    },
    computed: {
        canSave() {
            return this.templates.filter((t) => t.isAllowedToEdit).length > 0;
        },
    },
    created() {
        this.load();
    },
    methods: {
        setStationery(template, stationery) {
            if (stationery === null) {
                template.stationeryId = null;
                return;
            }
            template.stationeryId = stationery.id;
        },
        deleteStationery(stationery) {
            ClubStationeryRepository.remove(stationery.id).then(() => {
                this.$notify.success('saveChanges.success');
                this.load();
            }).catch(() => {
                this.$notify.error('saveChanges.error');
            }).finally(() => {
                this.stationeryForDeletion = null;
            });
        },
        save() {
            this.pending = true;
            ClubInvoiceTemplateRepository.update(this.clubId, this.templates)
                .then(() => {
                    this.$notify.success('saveChanges.success');
                    this.load();
                })
                .catch(() => {
                    this.$notify.error('saveChanges.error');
                })
                .finally(() => this.pending = false);
        },
        addConfig() {
            this.showNewConfigDialog = true;
        },
        load() {
            this.loading = true;
            Promise.all([
                ClubStationeryRepository.findSimpleByClub(this.clubId),
                ClubStationeryRepository.findInvoiceByClub(this.clubId),
                ClubInvoiceTemplateRepository.findByClub(this.clubId),
            ])
                .then(([simpleStationeries, invoiceStationeries, templates]) => {
                    this.simpleStationeries = simpleStationeries;
                    this.invoiceStationeries = invoiceStationeries;
                    this.templates = templates;
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>

<style scoped>
.kgr-card span {
    text-align: left;
}
hr.kgr-separator {
    margin-top: 50px;
    margin-bottom: 40px;
}
div.spacer {
    height: 30px;
}
</style>
